// Hook for handling avatar URL signing for S3 images
import { useState, useEffect, useMemo } from 'react';
import { getSignedUrlForS3Path } from '../utils/s3PathUtils';
import voiceActressDefault from '../assets/voiceActress.png';

/**
 * Custom hook to handle avatar URL signing for S3 images
 * @param {string} avatarUrl - The original avatar URL (may be S3 or signed)
 * @param {string} userId - The user ID for signing S3 URLs
 * @param {string} fallbackImage - Optional custom fallback image (defaults to voiceActress.png)
 * @returns {object} { signedUrl, loading, error }
 */
const useSignedAvatar = (avatarUrl, userId, fallbackImage = voiceActressDefault) => {
  const [signedUrl, setSignedUrl] = useState(fallbackImage);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Helper to check if URL needs signing
  const isS3Url = useMemo(() => {
    if (!avatarUrl || typeof avatarUrl !== 'string') return false;
    return avatarUrl.includes('s3.') ||
      avatarUrl.includes('amazonaws.com') ||
      avatarUrl.includes('aai-users/') ||
      avatarUrl.includes('aai-actors/');
  }, [avatarUrl]);

  // Check if URL is already signed
  const isSignedUrl = useMemo(() => {
    if (!avatarUrl || typeof avatarUrl !== 'string') return false;
    return avatarUrl.includes('X-Amz-Signature=');
  }, [avatarUrl]);

  useEffect(() => {
    let mounted = true;

    const processAvatarUrl = async () => {
      try {
        setLoading(true);
        setError(null);

        // No avatar URL provided - use fallback
        if (!avatarUrl) {
          setSignedUrl(fallbackImage);
          setLoading(false);
          return;
        }

        // URL is already signed - use as is
        if (isSignedUrl) {
          setSignedUrl(avatarUrl);
          setLoading(false);
          return;
        }

        // URL is S3 and needs signing
        if (isS3Url && userId) {
          try {
            const signed = await getSignedUrlForS3Path(avatarUrl, userId);
            if (mounted) {
              setSignedUrl(signed);
            }
          } catch (err) {
            console.error('[useSignedAvatar] Failed to sign S3 URL:', err);
            if (mounted) {
              setError(err);
              setSignedUrl(fallbackImage);
            }
          }
        } else if (isS3Url && !userId) {
          // S3 URL but no userId - can't sign, use fallback
          console.warn('[useSignedAvatar] S3 URL detected but no userId provided for signing');
          setSignedUrl(fallbackImage);
        } else {
          // Regular URL - use as is
          setSignedUrl(avatarUrl);
        }
      } catch (err) {
        console.error('[useSignedAvatar] Error processing avatar URL:', err);
        if (mounted) {
          setError(err);
          setSignedUrl(fallbackImage);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    processAvatarUrl();

    return () => {
      mounted = false;
    };
  }, [avatarUrl, userId, isS3Url, isSignedUrl, fallbackImage]);

  return {
    signedUrl,
    loading,
    error,
    isS3: isS3Url,
    isSigned: isSignedUrl
  };
};

export default useSignedAvatar;
